import React from "react";
import { Download } from "lucide-react";
import Button from "../../../common/Button";

/**
 * ExportButton Component
 *
 * Exports data to CSV format with automatic download
 *
 * @param {Array} data - Array of data objects to export
 * @param {String} filename - Base filename (without extension or date)
 * @param {Array} columns - Array of column configurations:
 *   {
 *     key: string,        // Key in data object
 *     label: string,      // Column header in CSV
 *     format: function    // Optional formatter (value, row) => string
 *   }
 * @param {String} className - Optional additional CSS class
 * @param {Boolean} disabled - Disable button
 */
export default function ExportButton({
  data = [],
  filename = "export",
  columns = [],
  className = "",
  disabled = false,
}) {
  const handleExport = () => {
    if (!data || data.length === 0) {
      alert("No data available to export");
      return;
    }

    if (!columns || columns.length === 0) {
      alert("No columns configured for export");
      return;
    }

    try {
      // Generate CSV header
      const headers = columns.map((col) => col.label).join(",");
      let csvContent = headers + "\n";

      // Generate CSV rows
      data.forEach((row) => {
        const rowValues = columns.map((col) => {
          let value = row[col.key];

          // Apply custom formatter if provided
          if (col.format && typeof col.format === "function") {
            value = col.format(value, row);
          }

          // Handle null/undefined
          if (value === null || value === undefined) {
            return "";
          }

          // Convert to string
          value = String(value);

          // Escape double quotes and wrap in quotes if contains comma, quote, or newline
          if (
            value.includes(",") ||
            value.includes('"') ||
            value.includes("\n")
          ) {
            value = '"' + value.replace(/"/g, '""') + '"';
          } else {
            value = '"' + value + '"';
          }

          return value;
        });

        csvContent += rowValues.join(",") + "\n";
      });

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      // Format date as YYYY-MM-DD
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];

      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}_${dateStr}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting data:", error);
      alert("Failed to export data. Please try again.");
    }
  };

  return (
    <Button
      className={`button--icon ${className}`}
      onClick={handleExport}
      disabled={disabled || !data || data.length === 0}
    >
      <Download size={18} style={{ marginRight: "6px" }} />
      Export
    </Button>
  );
}
